import React, { FC, useEffect } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { Box, Container, Typography } from '@material-ui/core';
import cx from 'classnames';
import { format, isEqual } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import DownloadIcon from '../../assets/icons/DownloadIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import { Loader } from '../../components/Loader';
import { CardWrapper } from '../../components/UI/CardWrapper';
import { CustomButton } from '../../components/UI/CustomButton';
import { NON_RENEWING_SUBSCRIPTION_KEY } from '../../const';
import { GET_SUBSCRIPTION_INFO_THANK_YOU_PAGE } from '../../graphql/prices';
import { colorsPalette } from '../../theme';
import { Query, SubscriptionChargebeeThankYouInfo } from '../../types';
import { translate, updateUserComPlanInfo } from '../../utils/helpers';
import { useAuth } from '../../utils/hooks';
import { Header } from '../DiscoverPage/components/Header';
import useStyles from './styles';

const formatDataToCard = (data: SubscriptionChargebeeThankYouInfo, t: TFunction) => {
  const isMultiple = !!(data?.number_of_learners && data?.number_of_learners > 1);

  const priceVal = data?.total_price ? data.total_price.split(' ') : [];
  const priceFormatOptions = { style: 'currency', currency: priceVal[1] ? priceVal[1] : 'GBR' };
  const numberFormat = new Intl.NumberFormat('en-US', priceFormatOptions);

  let startDate = translate('page.subscription.notPayedYet');

  const date = data?.billing_starts ? new Date(data.billing_starts) : '';

  let renewsDate: Date | null = null;
  try {
    renewsDate =
      data?.renews_automaticly_on && !isNaN(Date.parse(data?.renews_automaticly_on))
        ? new Date(data?.renews_automaticly_on)
        : null;
  } catch (e) {}

  try {
    startDate = date ? format(new Date(date), 'do LLLL uuuu') : startDate;
  } catch (e) {}

  const isRenewsOnVisible = !!date && !!renewsDate ? !isEqual(date, renewsDate) : false;

  let renewsOn = '';

  if (renewsDate && isRenewsOnVisible) {
    renewsOn = format(renewsDate, 'do LLLL uuuu');
  } else if (data?.renews_automaticly_on === NON_RENEWING_SUBSCRIPTION_KEY) {
    renewsOn = t('page.subscription.nonRenewing');
  }

  return {
    subscriptionType: data?.type,
    ...(isMultiple && { organizationName: data?.organization_name }),
    numberOfLearners: data?.number_of_learners,
    totalPrice: priceVal[0] && priceVal[1] ? numberFormat.format(Number(priceVal[0])) : '',
    ...(data?.free_trial_period?.count &&
      data?.free_trial_period?.unit && {
        freeTrial: translate(`common.${data.free_trial_period.unit}`, {
          count: data.free_trial_period.count,
        }),
      }),
    billingStarts: startDate,
    paymentFrequency:
      data?.payment_frequency === '1 year'
        ? translate('page.subscription.annually')
        : translate('page.subscription.monthly'),
    renewsOn,
  };
};

interface ThankYouPageProps {
  tenantLinkedInId?: string | null;
}

const ThankYouPage: FC<ThankYouPageProps> = ({ tenantLinkedInId }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { isInitialized } = useAuth();

  const { data: subscriptionData, loading } = useQuery<
    Pick<Query, 'getSubscriptionInfoThankYouPage'>
  >(GET_SUBSCRIPTION_INFO_THANK_YOU_PAGE, {
    skip: !isInitialized,
  });
  const subscriptionInfo = subscriptionData?.getSubscriptionInfoThankYouPage;

  const cardData = subscriptionInfo ? formatDataToCard(subscriptionInfo, t) : null;

  // update plan and subscription ends info on user.com
  useEffect(() => {
    if (subscriptionData?.getSubscriptionInfoThankYouPage) {
      const data = subscriptionData.getSubscriptionInfoThankYouPage;

      updateUserComPlanInfo({
        plan: data?.type,
        renewDate: data?.renews_automaticly_on,
      });
    }
  }, [subscriptionData]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box className={classes.wrapper}>
      <Header title={t('page.subscription.title')} titleClass={classes.headerTitle}>
        <Container>
          <Typography className={classes.subtitle}>{t('page.subscription.subTitle')}</Typography>
        </Container>
      </Header>
      <Container className={classes.container}>
        <Box className={cx(classes.cardContainer, classes.subscriptionInfoContainer)}>
          <CardWrapper wrapperClass={classes.lightCard}>
            <Typography variant="h2">{t('page.subscription.subscriotionDetails')}</Typography>
            <Typography className={classes.cardSubtitle}>
              {t('page.subscription.invoiceDetails')}
            </Typography>
            <ul className={cx(classes.subscrInfoList)}>
              {cardData &&
                Object.keys(cardData).map((item) =>
                  cardData[item] ? (
                    <li id={item} className={classes.subsctListItem} key={item}>
                      <Typography className={classes.subsctListItemTitle}>
                        {t(`page.subscription.${item}`)}
                      </Typography>
                      <div className={classes.subscrItem}>{cardData?.[item]}</div>
                    </li>
                  ) : null,
                )}
            </ul>
            {!!subscriptionInfo?.invoice_link && (
              <a href={subscriptionInfo.invoice_link}>
                <CustomButton color="secondary" btnClass={classes.button}>
                  <DownloadIcon className={classes.buttonIcon} color={colorsPalette.white} />
                  {t('button.downloadInvoice')}
                </CustomButton>
              </a>
            )}
          </CardWrapper>
          <Typography className={classes.accountInfo}>
            {t('page.subscription.updateSubscriotion')}{' '}
            <Link className={classes.accountLink} to="/my-account">
              {t('page.subscription.yourAccount')}
            </Link>
            .
          </Typography>
        </Box>

        <Box className={cx(classes.cardContainer, classes.additionalInfoContainer)}>
          {subscriptionInfo?.number_of_learners && subscriptionInfo?.number_of_learners > 2 ? (
            <Box className={classes.darkCard}>
              <Typography variant="h2">{t('page.subscription.addLearners')}</Typography>
              <Typography className={classes.cardSubtitle}>
                {t('page.subscription.addLearnersInfo')}
              </Typography>
              <Link to="/my-account/teams">
                <CustomButton>{t('button.inviteLearners')}</CustomButton>
              </Link>
            </Box>
          ) : (
            <Box className={classes.darkCard}>
              <Typography variant="h2">{t('page.subscription.startLearning')}</Typography>
              <Typography className={classes.cardSubtitle}>
                {t('page.subscription.startLearningInfo')}
              </Typography>
              <Link to="/discover">
                <CustomButton>{t('button.startLearning')}</CustomButton>
              </Link>
            </Box>
          )}
          <Box className={classes.darkCard}>
            <Typography variant="h2">{t('page.subscription.stayUp')}</Typography>
            <Typography className={classes.cardSubtitle}>
              {t('page.subscription.stayUpInfo')}
            </Typography>
            <a
              href={`https://www.linkedin.com/company/${tenantLinkedInId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CustomButton color="secondary" btnClass={classes.button}>
                <LinkedInIcon
                  color={colorsPalette.linkedInBlue}
                  className={cx(classes.buttonIcon, classes.linkedInIcon)}
                />
                {t('button.followUslinkedin')}
              </CustomButton>
            </a>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ThankYouPage;
